import {
  useState,
  useCallback,
  useEffect,
  useRef,
  type ReactNode,
} from "react";
import { clampTime } from "@src/packages/utils/time";
import { View, Text, Platform, ScrollView, TextInput } from "react-native";
import {
  FileText,
  Check,
  AlertCircle,
  FolderOpen,
  Shield,
  PenTool,
  Receipt,
  Edit3,
  Trash2,
  Plus,
  RotateCcw,
} from "lucide-react-native";
import * as DocumentPicker from "expo-document-picker";
import {
  cardSurface,
  CompactActionButton,
  Dialog,
  DialogBodyText,
  DialogHeader,
  DocumentRow,
  PrimaryButton,
  IconButton,
  DateField,
  MoonTimeField,
  MoonFilterChip,
  BackLink,
  ActionBanner,
  TextField,
} from "@louise/moon-ui";
import {
  useGetUploadUrl,
  useUploadFile,
  useCompleteUpload,
} from "@src/packages/documents/hooks/useDocuments";
import { useApiClient } from "@src/packages/api/louise-api";
import {
  useCreateAppointment,
  useCreateMedication,
} from "@src/packages/care/useCalendarData";
import { serializeMedicationRoute } from "@src/packages/care/medication-route";
import { serializeMedicationForm } from "@src/packages/care/medication-form";
import { endDateFromDuration } from "@src/packages/document-vault/medication-duration";
import { findMedicationByName } from "@src/packages/medications/catalog/useMedicationCatalog";
import { withMedicationLookupDeadline } from "@src/packages/medications/catalog/request-timeout";
import {
  readFileForUpload,
  resolveUploadContentType,
} from "@src/packages/utils/fileUpload";
import { useTranslations } from "@src/packages/i18n";
import { EMBER } from "@louise/moon-ui/theme/tokens";
import {
  attachUploadJobSourceUpload,
  createUploadJob,
  createUploadJobIdempotencyKey,
  destinationForUploadContext,
  sendUploadJobCommand,
  type UploadJobSourceContext,
} from "@src/packages/documents/ingestion";
import {
  projectDocumentProcessingRunToPrefill,
  waitForDocumentProcessingRun,
  type DocumentProcessingRun,
} from "@src/packages/documents/ingestion/documentProcessingRun";
import {
  findDocumentValues,
  isSemenAnalysisRun,
} from "@src/packages/documents/ingestion/documentValues";
import {
  DocumentFileDropZone,
  type SelectedFile,
} from "@src/packages/document-vault/DocumentFileDropZone";
import {
  DocumentUploadProcessingStep,
  type DocumentUploadProcessingItem,
} from "@src/packages/document-vault/DocumentUploadProcessingStep";
import { DocumentValuesReview } from "@src/packages/document-vault/DocumentValuesReview";

export {
  DocumentFileDropZone,
  createSelectedFileFromWebFile,
} from "@src/packages/document-vault/DocumentFileDropZone";
export type { SelectedFile } from "@src/packages/document-vault/DocumentFileDropZone";

// ── Types ──

export type DocumentSlotInfo = {
  id: string;
  name: string;
  why: string;
} | null;

type Props = {
  visible: boolean;
  onClose: () => void;
  onSave?: (result: DocumentUploadSaveResult) => void;
  slot?: DocumentSlotInfo;
  defaultCategory?: string | null;
  defaultTags?: string[];
  ingestionContext?: UploadJobSourceContext;
  autoPickOnOpen?: boolean;
  /** General Documents uploads accept any file; clinical extractors keep their existing filter. */
  allowAnyFile?: boolean;
  initialFile?: SelectedFile | null;
  initialFiles?: SelectedFile[] | null;
  onMonitoringExtracted?: (data: Record<string, unknown>) => void;
};

export type DocumentUploadSaveResult = {
  fileName: string;
  resultType: string;
  category: string | null;
  uploadId: string | null;
  documentId: string | null;
};

type Step =
  | "pick"
  | "uploading"
  | "reading"
  | "review"
  | "values"
  | "monitoringParsed"
  | "processingError"
  | "saving"
  | "success";

type OCRData = Record<string, unknown>;

type MedicationRow = {
  id: string;
  name: string;
  dosage: string;
  dosageUnit: string;
  frequency: string;
  route: string;
  /** OCR-extracted delivery method (e.g. "pre_filled_pen"); serialized to the API form enum on create. */
  form: string;
  startDate: string;
  endDate: string;
  timeOfDay: string;
};

type AppointmentRow = {
  id: string;
  title: string;
  type: string; // "exam" | "procedure" | "consult" | "counseling"
  date: string;
  time: string;
  notes: string;
};

type DurableUploadJobItem = {
  jobId: string;
  fileItemId: string;
};

const APPOINTMENT_TYPE_OPTIONS = ["exam", "procedure", "consult", "counseling"];

type CategoryOption = {
  key: string;
  labelKey: string;
  Icon: typeof FolderOpen;
};

export const DOCUMENT_UPLOAD_CATEGORIES: CategoryOption[] = [
  {
    key: "medical_records",
    labelKey: "documents.category.medical_records.short_label",
    Icon: FolderOpen,
  },
  {
    key: "coverage_benefits",
    labelKey: "documents.category.coverage_benefits.short_label",
    Icon: Shield,
  },
  {
    key: "admin",
    labelKey: "documents.category.admin.short_label",
    Icon: Receipt,
  },
  {
    key: "legal_consent",
    labelKey: "documents.category.legal_consent.short_label",
    Icon: PenTool,
  },
];

/** Truthy only when the value is a non-empty string. The OCR fills missing
 *  fields with empty strings (not nulls), so plain `if (value)` checks
 *  misclassify documents — e.g. a prescription with `imagingType: ""` was
 *  routed to the imaging form. */
function hasContent(value: unknown): boolean {
  return typeof value === "string" && value.trim().length > 0;
}

function monitoringValueCount(data: OCRData | null): number {
  if (!data) return 0;
  const fieldCount = [
    "leftFollicleSizes",
    "rightFollicleSizes",
    "leftFollicleCount",
    "rightFollicleCount",
    "endometrialThickness",
  ].filter((key) => hasContent(data[key])).length;
  const resultCount = Array.isArray(data.results)
    ? data.results.filter((result) => {
        if (!result || typeof result !== "object") return false;
        return hasContent((result as Record<string, unknown>).marker);
      }).length
    : 0;
  return fieldCount + resultCount;
}

function formatFileSize(bytes?: number): string {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function revokeSelectedFileObjectUrl(file: SelectedFile) {
  if (Platform.OS !== "web" || !file.objectUrl || typeof URL === "undefined") {
    return;
  }
  URL.revokeObjectURL(file.objectUrl);
}

// Upload errors carry developer text ("Failed to fetch"), never copy meant for
// the patient, so the dialog always shows its own translated message and the raw one
// goes to the log.
function getDocumentUploadErrorMessage(error: unknown, fallback: string) {
  console.warn("Document upload failed:", error);
  return fallback;
}

// ── Editable field component ──

const fieldTextStyle = {
  fontSize: 14,
  color: EMBER.colors.text,
  fontFamily: "DMSans_400Regular",
  ...(Platform.OS === "web" && { outlineStyle: "none" as const }),
};

function Field({
  label,
  value,
  onChangeText,
  placeholder,
  multiline,
  onBlur,
  keyboardType,
  suffix,
  trailing,
}: {
  label: string;
  value: string;
  onChangeText: (v: string) => void;
  placeholder?: string;
  multiline?: boolean;
  onBlur?: () => void;
  keyboardType?: "default" | "decimal-pad" | "number-pad";
  /** The value's unit, written inside the field after it. */
  suffix?: string;
  /** A control inside the field after the value, such as a typed unit. */
  trailing?: ReactNode;
}) {
  return (
    <TextField
      label={label}
      value={value}
      onChangeText={onChangeText}
      onBlur={onBlur}
      placeholder={placeholder}
      notes={Boolean(multiline)}
      keyboardType={keyboardType}
      trailing={
        suffix || trailing ? (
          <View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
            {suffix ? (
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 14,
                  fontWeight: "500",
                  color: EMBER.colors.textLight,
                }}
              >
                {suffix}
              </Text>
            ) : null}
            {trailing}
          </View>
        ) : null
      }
    />
  );
}

/** A typed unit at the end of a field, after a thin divider. */
function FieldUnitInput({
  value,
  onChangeText,
  placeholder,
  accessibilityLabel,
}: {
  value: string;
  onChangeText: (v: string) => void;
  placeholder?: string;
  accessibilityLabel: string;
}) {
  return (
    <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
      <View
        style={{ width: 1, height: 20, backgroundColor: "rgba(0,0,0,0.08)" }}
      />
      <TextInput
        value={value}
        onChangeText={onChangeText}
        placeholder={placeholder}
        placeholderTextColor={EMBER.colors.textFaint}
        accessibilityLabel={accessibilityLabel}
        autoCapitalize="none"
        autoCorrect={false}
        style={{
          ...fieldTextStyle,
          width: 64,
          paddingVertical: 10,
          textAlign: "right",
          color: EMBER.colors.textLight,
        }}
      />
    </View>
  );
}

// ── Step 1: Pick file ──

function PickStep({
  slot,
  defaultCategory,
  onFilesSelected,
  errorMessage,
  autoPickOnOpen = true,
  allowAnyFile = false,
}: {
  slot: DocumentSlotInfo;
  defaultCategory?: string | null;
  onFilesSelected: (files: SelectedFile[], category: string | null) => void;
  errorMessage: string | null;
  autoPickOnOpen?: boolean;
  allowAnyFile?: boolean;
}) {
  const t = useTranslations();
  const autoPickHandledRef = useRef(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(
    slot ? null : (defaultCategory ?? null),
  );

  const handlePick = useCallback(async () => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: allowAnyFile ? "*/*" : ["application/pdf", "image/*"],
        copyToCacheDirectory: true,
        multiple: true,
      });

      if (!result.canceled && result.assets && result.assets.length > 0) {
        onFilesSelected(
          result.assets.map((file) => ({
            name: file.name,
            uri: file.uri,
            mimeType: resolveUploadContentType({
              name: file.name,
              uri: file.uri,
              mimeType: file.mimeType,
            }),
            size: file.size ?? undefined,
          })),
          selectedCategory,
        );
      }
    } catch (error) {
      console.error("Error picking document:", error);
    }
  }, [allowAnyFile, onFilesSelected, selectedCategory]);

  useEffect(() => {
    if (!autoPickOnOpen || autoPickHandledRef.current) return;
    autoPickHandledRef.current = true;
    void handlePick();
  }, [autoPickOnOpen, handlePick]);

  return (
    <>
      <DialogHeader
        title={slot ? slot.name : t("documents.upload.upload_a_document")}
        subtitle={
          slot
            ? slot.why
            : t(
                allowAnyFile
                  ? "documents.upload.add_any_file_to_your_care_record"
                  : "documents.upload.read_and_extract_key_details",
              )
        }
        style={{ marginBottom: 20 }}
      />

      {/* Error */}
      {errorMessage && <ErrorBanner message={errorMessage} />}

      {/* Category pills — only for general uploads */}
      {!slot && (
        <View
          style={{
            flexDirection: "row",
            flexWrap: "wrap",
            gap: 8,
            marginBottom: 16,
          }}
        >
          {DOCUMENT_UPLOAD_CATEGORIES.map((cat) => {
            const isSelected = selectedCategory === cat.key;
            return (
              <MoonFilterChip
                key={cat.key}
                label={t(cat.labelKey)}
                icon={cat.Icon}
                selected={isSelected}
                onPress={() => setSelectedCategory(isSelected ? null : cat.key)}
                showCheck={false}
              />
            );
          })}
        </View>
      )}

      {/* File picker area */}
      <DocumentFileDropZone
        onPick={handlePick}
        secondaryLabel={allowAnyFile ? "" : undefined}
        onFilesSelected={(files) => onFilesSelected(files, selectedCategory)}
      />
    </>
  );
}

function ProcessingErrorStep({
  fileName,
  message,
  onChooseAnother,
}: {
  fileName: string;
  message: string;
  onChooseAnother: () => void;
}) {
  const t = useTranslations();

  return (
    <>
      <View style={{ gap: 14 }}>
        <DialogHeader
          leading={
            <AlertCircle size={22} color={EMBER.colors.coral} strokeWidth={2} />
          }
          title={t("documents.upload.error_failed_process")}
          subtitle={message}
        />
        <View
          style={{
            width: "100%",
            flexDirection: "row",
            alignItems: "center",
            gap: 8,
          }}
        >
          <FileText
            size={14}
            color={EMBER.colors.textLight}
            strokeWidth={1.5}
          />
          <Text
            className="font-dm-sans"
            numberOfLines={1}
            style={{ flex: 1, fontSize: 12, color: EMBER.colors.textMid }}
          >
            {fileName}
          </Text>
        </View>
        <PrimaryButton
          label={t("documents.upload.failure.choose_another")}
          onPress={onChooseAnother}
          style={{ width: "100%", marginTop: 4 }}
        />
      </View>
    </>
  );
}

function QueuedDocumentsCard({ files }: { files: SelectedFile[] }) {
  const t = useTranslations();
  if (files.length === 0) return null;

  const visibleFiles = files.slice(0, 3);
  const hiddenFileCount = files.length - visibleFiles.length;

  return (
    <View style={{ gap: 8 }}>
      <View style={{ gap: 2 }}>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 13,
            fontWeight: "600",
            color: EMBER.colors.text,
          }}
        >
          {t("documents.upload.queued_documents_title")}
        </Text>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 11,
            lineHeight: 15,
            color: EMBER.colors.textLight,
          }}
        >
          {t("documents.upload.queued_documents_subtitle")}
        </Text>
      </View>
      <View style={{ gap: 6 }}>
        {visibleFiles.map((file) => (
          <View
            key={file.uri}
            style={{ flexDirection: "row", alignItems: "center", gap: 7 }}
          >
            <FileText
              size={14}
              color={EMBER.colors.textLight}
              strokeWidth={1.6}
            />
            <Text
              className="font-dm-sans"
              numberOfLines={1}
              style={{ flex: 1, fontSize: 12, color: EMBER.colors.text }}
            >
              {file.name}
            </Text>
          </View>
        ))}
        {hiddenFileCount > 0 ? (
          <Text
            className="font-dm-sans"
            style={{ fontSize: 11, color: EMBER.colors.textLight }}
          >
            {t("documents.upload.queued_documents_more", {
              params: { count: String(hiddenFileCount) },
            })}
          </Text>
        ) : null}
      </View>
    </View>
  );
}

function MonitoringParsedStep({
  fileName,
  valueCount,
  queuedFiles,
  saving,
  onContinue,
}: {
  fileName: string;
  valueCount: number;
  queuedFiles: SelectedFile[];
  saving: boolean;
  onContinue: () => void;
}) {
  const t = useTranslations();
  const hasValues = valueCount > 0;

  return (
    <>
      <View style={{ gap: 12 }}>
        <DialogHeader
          leading={
            <Check size={22} color={EMBER.colors.text} strokeWidth={2.4} />
          }
          title={t(
            hasValues
              ? "documents.upload.monitoring_parsed_title"
              : "documents.upload.monitoring_parsed_empty_title",
          )}
          subtitle={t(
            hasValues
              ? "documents.upload.monitoring_parsed_subtitle"
              : "documents.upload.monitoring_parsed_empty_subtitle",
          )}
        />
        <View
          style={{
            width: "100%",
            flexDirection: "row",
            alignItems: "center",
            gap: 8,
          }}
        >
          <FileText
            size={14}
            color={EMBER.colors.textLight}
            strokeWidth={1.5}
          />
          <Text
            className="font-dm-sans"
            numberOfLines={1}
            style={{ flex: 1, fontSize: 12, color: EMBER.colors.textMid }}
          >
            {fileName}
          </Text>
        </View>
        {hasValues ? (
          <Text
            className="font-dm-sans"
            style={{ fontSize: 13, color: EMBER.colors.textLight }}
          >
            {t("documents.upload.monitoring_parsed_count", {
              params: { count: String(valueCount) },
            })}
          </Text>
        ) : null}
        {queuedFiles.length > 0 ? (
          <View style={{ width: "100%", marginTop: 4 }}>
            <QueuedDocumentsCard files={queuedFiles} />
          </View>
        ) : null}
        <PrimaryButton
          label={
            saving
              ? t("documents.upload.saving")
              : t("documents.upload.monitoring_parsed_action")
          }
          onPress={onContinue}
          loading={saving}
          disabled={saving}
          style={{ width: "100%", marginTop: 4 }}
        />
      </View>
    </>
  );
}

function PrescriptionReview({
  medications,
  setMedications,
  appointments,
  setAppointments,
  prescriber,
  setPrescriber,
  date,
  setDate,
  clinic,
  setClinic,
}: {
  medications: MedicationRow[];
  setMedications: (rows: MedicationRow[]) => void;
  appointments: AppointmentRow[];
  setAppointments: (rows: AppointmentRow[]) => void;
  prescriber: string;
  setPrescriber: (v: string) => void;
  date: string;
  setDate: (v: string) => void;
  clinic: string;
  setClinic: (v: string) => void;
}) {
  const t = useTranslations();
  const updateRow = (id: string, field: keyof MedicationRow, value: string) => {
    setMedications(
      medications.map((r) => (r.id === id ? { ...r, [field]: value } : r)),
    );
  };

  const removeRow = (id: string) => {
    setMedications(medications.filter((r) => r.id !== id));
  };

  const addRow = () => {
    setMedications([
      ...medications,
      {
        id: `med-${Date.now()}`,
        name: "",
        dosage: "",
        dosageUnit: "",
        frequency: "",
        route: "",
        form: "",
        startDate: "",
        endDate: "",
        timeOfDay: "",
      },
    ]);
  };

  return (
    <View style={{ gap: 14 }}>
      <View style={{ flexDirection: "row", gap: 10 }}>
        <View style={{ flex: 1 }}>
          <DateField
            label={t("documents.upload.fields.date")}
            value={date}
            onChange={setDate}
          />
        </View>
        <View style={{ flex: 1 }}>
          <Field
            label={t("documents.upload.fields.prescriber")}
            value={prescriber}
            onChangeText={setPrescriber}
          />
        </View>
      </View>
      <Field
        label={t("documents.upload.fields.clinic")}
        value={clinic}
        onChangeText={setClinic}
      />

      {/* Medication rows */}
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 11,
          fontWeight: "600",
          color: EMBER.colors.textLight,
          textTransform: "uppercase",
          letterSpacing: 0.5,
        }}
      >
        {t("documents.upload.medications_count", {
          params: { count: String(medications.length) },
        })}
      </Text>
      {medications.map((row) => (
        <View
          key={row.id}
          style={{ ...cardSurface({ variant: "panel" }), padding: 12, gap: 8 }}
        >
          <View
            style={{
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 14,
                fontWeight: "600",
                color: EMBER.colors.text,
                flex: 1,
              }}
              numberOfLines={1}
            >
              {row.name || t("documents.upload.unnamed_medication")}
            </Text>
            <IconButton
              accessibilityLabel={t("documents.upload.remove_this_medication")}
              action="remove_medication_row"
              hitSlop={8}
              onPress={() => removeRow(row.id)}
              size={14}
              surface="bare"
            >
              <Trash2 size={14} color={EMBER.colors.coral} strokeWidth={1.5} />
            </IconButton>
          </View>
          <Field
            label={t("documents.upload.fields.medication_name")}
            value={row.name}
            onChangeText={(v) => updateRow(row.id, "name", v)}
          />
          {/* The dose with its unit typed at the end of the same field. */}
          <Field
            label={t("documents.upload.fields.dosage")}
            value={row.dosage}
            onChangeText={(v) => updateRow(row.id, "dosage", v)}
            trailing={
              <FieldUnitInput
                value={row.dosageUnit}
                onChangeText={(v) => updateRow(row.id, "dosageUnit", v)}
                placeholder={t("documents.upload.fields.unit_placeholder")}
                accessibilityLabel={t("documents.upload.fields.unit")}
              />
            }
          />
          <View style={{ flexDirection: "row", gap: 8 }}>
            <View style={{ flex: 1 }}>
              <Field
                label={t("documents.upload.fields.frequency")}
                value={row.frequency}
                onChangeText={(v) => updateRow(row.id, "frequency", v)}
                placeholder={t("documents.upload.fields.frequency_placeholder")}
              />
            </View>
            <View style={{ flex: 1 }}>
              <Field
                label={t("documents.upload.fields.route")}
                value={row.route}
                onChangeText={(v) => updateRow(row.id, "route", v)}
                placeholder={t("documents.upload.fields.route_placeholder")}
              />
            </View>
          </View>
          <View style={{ flexDirection: "row", gap: 8 }}>
            <View style={{ flex: 1 }}>
              <DateField
                label={t("documents.upload.fields.start_date")}
                value={row.startDate}
                onChange={(v) => updateRow(row.id, "startDate", v)}
              />
            </View>
            <View style={{ flex: 1 }}>
              <DateField
                label={t("documents.upload.fields.end_date")}
                value={row.endDate}
                onChange={(v) => updateRow(row.id, "endDate", v)}
              />
            </View>
          </View>
          <MoonTimeField
            label={t("documents.upload.fields.time")}
            value={row.timeOfDay}
            onChange={(v) => updateRow(row.id, "timeOfDay", v)}
          />
        </View>
      ))}
      <CompactActionButton
        label={t("documents.upload.add_medication")}
        action="documents.upload.add_medication"
        variant="secondary"
        size="md"
        leadingIcon={Plus}
        onPress={addRow}
      />

      {/* Appointment rows */}
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 11,
          fontWeight: "600",
          color: EMBER.colors.textLight,
          textTransform: "uppercase",
          letterSpacing: 0.5,
          marginTop: 8,
        }}
      >
        {t("documents.upload.appointments_count", {
          params: { count: String(appointments.length) },
        })}
      </Text>
      {appointments.map((row) => (
        <View
          key={row.id}
          style={{ ...cardSurface({ variant: "panel" }), padding: 12, gap: 8 }}
        >
          <View
            style={{
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 14,
                fontWeight: "600",
                color: EMBER.colors.text,
                flex: 1,
              }}
              numberOfLines={1}
            >
              {row.title || t("documents.upload.unnamed_appointment")}
            </Text>
            <IconButton
              accessibilityLabel={t("documents.upload.remove_this_appointment")}
              action="remove_appointment_row"
              hitSlop={8}
              onPress={() =>
                setAppointments(appointments.filter((r) => r.id !== row.id))
              }
              size={14}
              surface="bare"
            >
              <Trash2 size={14} color={EMBER.colors.coral} strokeWidth={1.5} />
            </IconButton>
          </View>
          <Field
            label={t("documents.upload.fields.appointment_title")}
            value={row.title}
            onChangeText={(v) =>
              setAppointments(
                appointments.map((r) =>
                  r.id === row.id ? { ...r, title: v } : r,
                ),
              )
            }
          />
          <View style={{ flexDirection: "row", gap: 8 }}>
            <View style={{ flex: 1 }}>
              <Field
                label={t("documents.upload.fields.appointment_type")}
                value={row.type}
                onChangeText={(v) =>
                  setAppointments(
                    appointments.map((r) =>
                      r.id === row.id ? { ...r, type: v } : r,
                    ),
                  )
                }
                placeholder={t(
                  "documents.upload.fields.appointment_type_placeholder",
                )}
              />
            </View>
            <View style={{ flex: 1 }}>
              <DateField
                label={t("documents.upload.fields.date")}
                value={row.date}
                onChange={(v) =>
                  setAppointments(
                    appointments.map((r) =>
                      r.id === row.id ? { ...r, date: v } : r,
                    ),
                  )
                }
              />
            </View>
          </View>
          <View style={{ flexDirection: "row", gap: 8 }}>
            <View style={{ flex: 1 }}>
              <Field
                label={t("documents.upload.fields.time")}
                value={row.time}
                onChangeText={(v) =>
                  setAppointments(
                    appointments.map((r) =>
                      r.id === row.id ? { ...r, time: v } : r,
                    ),
                  )
                }
                onBlur={() => {
                  if (!row.time) return;
                  const clamped = clampTime(row.time.trim());
                  setAppointments(
                    appointments.map((r) =>
                      r.id === row.id ? { ...r, time: clamped ?? "" } : r,
                    ),
                  );
                }}
                placeholder={t("documents.upload.fields.time_placeholder")}
              />
            </View>
            <View style={{ flex: 1 }}>
              <Field
                label={t("documents.upload.fields.notes")}
                value={row.notes}
                onChangeText={(v) =>
                  setAppointments(
                    appointments.map((r) =>
                      r.id === row.id ? { ...r, notes: v } : r,
                    ),
                  )
                }
              />
            </View>
          </View>
        </View>
      ))}
      <CompactActionButton
        label={t("documents.upload.add_appointment")}
        action="documents.upload.add_appointment"
        variant="secondary"
        size="md"
        leadingIcon={Plus}
        onPress={() => {
          setAppointments([
            ...appointments,
            {
              id: `appt-${Date.now()}`,
              title: "",
              type: "exam",
              date: "",
              time: "",
              notes: "",
            },
          ]);
        }}
      />
    </View>
  );
}

// ── Shared UI components ──

// The success step's supporting lines, under its title.
const SUCCESS_DETAIL_STYLE = { marginTop: 0 };

function ErrorBanner({ message }: { message: string }) {
  return (
    <ActionBanner
      variant="important"
      message={message}
      style={{ marginBottom: 16 }}
    />
  );
}

// ── Main orchestrator ──

function ModalContent({
  onClose,
  onSave,
  slot,
  defaultCategory,
  defaultTags = [],
  ingestionContext,
  autoPickOnOpen = true,
  allowAnyFile = false,
  initialFile,
  initialFiles,
  onMonitoringExtracted,
}: {
  onClose: () => void;
  onSave?: (result: DocumentUploadSaveResult) => void;
  slot: DocumentSlotInfo;
  defaultCategory?: string | null;
  defaultTags?: string[];
  ingestionContext?: UploadJobSourceContext;
  autoPickOnOpen?: boolean;
  allowAnyFile?: boolean;
  initialFile?: SelectedFile | null;
  initialFiles?: SelectedFile[] | null;
  onMonitoringExtracted?: (data: Record<string, unknown>) => void;
}) {
  const t = useTranslations();
  const [step, setStep] = useState<Step>("pick");
  const [selectedFile, setSelectedFile] = useState<SelectedFile | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(
    defaultCategory ?? null,
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [monitoringExtractedData, setMonitoringExtractedData] =
    useState<OCRData | null>(null);
  // The run whose values the patient confirms before anything is saved.
  const [valuesRun, setValuesRun] = useState<DocumentProcessingRun | null>(
    null,
  );
  const [savedValueCount, setSavedValueCount] = useState(0);
  const [queuedFiles, setQueuedFiles] = useState<SelectedFile[]>([]);
  const uploadIdRef = useRef<string | null>(null);
  const canonicalSourceAttachedRef = useRef(false);
  const canonicalRunReadyRef = useRef(false);
  const durableUploadJobIdRef = useRef<string | null>(null);
  const durableUploadJobItemsByNameRef = useRef<Map<string, string[]>>(
    new Map(),
  );
  const durableUploadJobItemRef = useRef<DurableUploadJobItem | null>(null);
  const primarySaveCompletedRef = useRef(false);
  const createdDocumentIdRef = useRef<string | null>(null);
  const savedMedicationRowIdsRef = useRef<Set<string>>(new Set());
  const savedAppointmentRowIdsRef = useRef<Set<string>>(new Set());
  const initialFileHandledRef = useRef<string | null>(null);
  const queuedFilesRef = useRef<SelectedFile[]>([]);

  const hasInitialFiles =
    Boolean(initialFiles && initialFiles.length > 0) || Boolean(initialFile);
  const shouldAutoPickOnOpen = autoPickOnOpen && !hasInitialFiles;
  const getIngestionContext = useCallback(
    (category: string | null): UploadJobSourceContext => {
      const targetHints = Array.from(
        new Set(
          [
            ...(ingestionContext?.targetHints ?? []),
            ...(defaultTags ?? []),
            slot?.id,
            category,
          ].filter((value): value is string => Boolean(value)),
        ),
      );

      return {
        ...ingestionContext,
        targetHints,
        allowBackgroundProcessing:
          ingestionContext?.allowBackgroundProcessing ?? !onMonitoringExtracted,
        // Nothing a document says is saved until the patient confirms it,
        // so the server never accepts its values on its own.
        allowAutomaticPromotion: false,
      };
    },
    [defaultTags, ingestionContext, onMonitoringExtracted, slot],
  );

  // Form state for review — prescription / treatment plan
  const [medicationRows, setMedicationRows] = useState<MedicationRow[]>([]);
  const [prescriber, setPrescriber] = useState("");
  const [prescriptionDate, setPrescriptionDate] = useState("");
  const [prescriptionClinic, setPrescriptionClinic] = useState("");
  const [appointmentRows, setAppointmentRows] = useState<AppointmentRow[]>([]);

  const resetExtractedState = useCallback(() => {
    uploadIdRef.current = null;
    canonicalSourceAttachedRef.current = false;
    canonicalRunReadyRef.current = false;
    durableUploadJobItemRef.current = null;
    primarySaveCompletedRef.current = false;
    createdDocumentIdRef.current = null;
    savedMedicationRowIdsRef.current = new Set();
    savedAppointmentRowIdsRef.current = new Set();
    setMonitoringExtractedData(null);
    setValuesRun(null);
    setSavedValueCount(0);
    setMedicationRows([]);
    setPrescriber("");
    setPrescriptionDate("");
    setPrescriptionClinic("");
    setAppointmentRows([]);
  }, []);

  // API hooks
  const getUploadUrl = useGetUploadUrl();
  const uploadFile = useUploadFile();
  const completeUpload = useCompleteUpload();
  const createMedicationMutation = useCreateMedication();
  const createAppointmentMutation = useCreateAppointment();

  const { client, patientId } = useApiClient();

  const registerDurableUploadJob = useCallback(
    (job: { id: string; items: { id: string; name: string }[] }) => {
      durableUploadJobIdRef.current = job.id;
      const itemsByName = new Map<string, string[]>();
      job.items.forEach((item) => {
        const itemIds = itemsByName.get(item.name) ?? [];
        itemIds.push(item.id);
        itemsByName.set(item.name, itemIds);
      });
      durableUploadJobItemsByNameRef.current = itemsByName;
    },
    [],
  );

  const takeDurableUploadJobItem = useCallback((fileName: string) => {
    const itemIds = durableUploadJobItemsByNameRef.current.get(fileName);
    const fileItemId = itemIds?.shift();
    if (!fileItemId || !durableUploadJobIdRef.current) return null;
    return {
      jobId: durableUploadJobIdRef.current,
      fileItemId,
    };
  }, []);

  // Prefill the medication and appointment rows a document proposes. Its
  // measured values are not prefilled into a form: they go through the
  // values confirmation, which saves them through the run itself.
  const prefillFromOCR = useCallback((data: OCRData) => {
    const date =
      (data.documentDate as string) || (data.prescriptionDate as string) || "";
    const provider =
      (data.providerName as string) ||
      (data.clinicName as string) ||
      (data.provider as string) ||
      "";

    const meds = (data.medications as Record<string, string>[]) || [];
    if (meds.length > 0) {
      setMedicationRows(
        meds.map((m, i) => ({
          id: `med-${i}`,
          name: m.medicationName || "",
          dosage: m.dosage || "",
          dosageUnit: m.dosageUnit || "",
          frequency: m.frequency || "",
          route: m.route || "",
          form: m.deliveryMethod || "",
          startDate: m.startDate || "",
          endDate:
            m.endDate ||
            endDateFromDuration(m.startDate || "", m.duration || ""),
          timeOfDay: m.timeOfDay || "",
        })),
      );
      setPrescriber((data.prescriber as string) || "");
      setPrescriptionDate(date);
      setPrescriptionClinic(provider);
    }

    const appts = (data.appointments as Record<string, string>[]) || [];
    if (appts.length > 0) {
      setAppointmentRows(
        appts.map((a, i) => ({
          id: `appt-${i}`,
          title: a.title || "",
          type:
            a.type && APPOINTMENT_TYPE_OPTIONS.includes(a.type)
              ? a.type
              : "exam",
          date: a.date || "",
          time: a.time || "",
          notes: a.notes || "",
        })),
      );
      setPrescriptionDate(date);
      setPrescriptionClinic(provider);
    }

    return meds.length > 0 || appts.length > 0;
  }, []);

  // Handle file selection — start upload + OCR
  const failedProcessMessage = t("documents.upload.error_failed_process");
  const handleFileSelected = useCallback(
    async (
      file: SelectedFile,
      category: string | null,
      durableJobItem: DurableUploadJobItem | null = null,
    ) => {
      resetExtractedState();
      setSelectedFile(file);
      setSelectedCategory(category);
      setErrorMessage(null);
      setStep("uploading");

      const destination = destinationForUploadContext({
        slotId: slot?.id,
        category,
      });
      const contentType = resolveUploadContentType(file);

      try {
        if (!patientId) {
          throw new Error("Document upload requires an active patient");
        }

        // Every source must have a durable job/item before bytes are
        // transferred. The processing worker, not this modal, owns the
        // document-intelligence run after source acknowledgement.
        durableUploadJobItemRef.current =
          durableJobItem ?? takeDurableUploadJobItem(file.name);
        if (!durableUploadJobItemRef.current) {
          const job = await createUploadJob(client, patientId, {
            clientRequestId: createUploadJobIdempotencyKey(),
            source: "picker",
            sourceChannel: "patient_ui",
            intent: slot || onMonitoringExtracted ? "prefill" : "dump_anything",
            sourceContext: getIngestionContext(category),
            items: [
              {
                name: file.name,
                contentType,
                sizeBytes: file.size ?? null,
                destination,
              },
            ],
          });
          registerDurableUploadJob(job);
          durableUploadJobItemRef.current = takeDurableUploadJobItem(file.name);
        }
        if (!durableUploadJobItemRef.current) {
          throw new Error("Document upload job item could not be created");
        }

        // Step 1: Upload file to S3
        const { uploadId, uploadUrl } = await getUploadUrl.mutateAsync({
          name: file.name,
          contentType,
        });

        const fileData = await readFileForUpload({
          uri: file.uri,
          name: file.name,
          mimeType: file.mimeType,
        });
        await uploadFile.mutateAsync({
          uploadUrl,
          file: fileData,
          contentType,
        });

        await completeUpload.mutateAsync(uploadId);
        uploadIdRef.current = uploadId;
        setStep("reading");

        const currentDurableJobItem = durableUploadJobItemRef.current;
        if (!currentDurableJobItem) {
          throw new Error("Document upload job item is unavailable");
        }
        await attachUploadJobSourceUpload(
          client,
          patientId,
          currentDurableJobItem.jobId,
          currentDurableJobItem.fileItemId,
          { uploadId },
        );
        canonicalSourceAttachedRef.current = true;

        // The original file is durably saved from here on, whatever the
        // reading below finds.
        const finishAsSaved = () => {
          onSave?.({
            fileName: file.name,
            resultType: "general",
            category,
            uploadId,
            documentId: null,
          });
          setStep("success");
          if (queuedFiles.length === 0) {
            setTimeout(() => onClose(), 1200);
          }
        };

        // Contextual uploads may wait briefly for the canonical run. General
        // uploads continue through the global activity surface, which invites
        // the patient to review the values once the document is read.
        const needsContextualPrefill = Boolean(slot || onMonitoringExtracted);
        if (!needsContextualPrefill) {
          finishAsSaved();
          return;
        }

        const result = await waitForDocumentProcessingRun(
          client,
          patientId,
          currentDurableJobItem.jobId,
          currentDurableJobItem.fileItemId,
        );
        const runSucceeded = result?.status === "succeeded";
        canonicalRunReadyRef.current = runSucceeded;
        if (result && !runSucceeded) {
          setErrorMessage(failedProcessMessage);
          setStep("processingError");
          return;
        }
        const projectedResult =
          result && runSucceeded
            ? projectDocumentProcessingRunToPrefill(result, file.name)
            : null;

        if (onMonitoringExtracted) {
          if (projectedResult) prefillFromOCR(projectedResult);
          setMonitoringExtractedData(projectedResult);
          onMonitoringExtracted(projectedResult ?? {});
          setStep("monitoringParsed");
          return;
        }

        if (result && projectedResult) {
          const readsAsResults =
            result.classificationCode === "lab_result" ||
            result.classificationCode === "imaging_result";
          // A document's values wait for the patient's confirmation; a
          // document of medications and appointments keeps its own review.
          if (findDocumentValues(result, null).length > 0 || readsAsResults) {
            setValuesRun(result);
            setStep("values");
            return;
          }
          if (prefillFromOCR(projectedResult)) {
            setStep("review");
            return;
          }
        }

        finishAsSaved();
      } catch (error) {
        console.warn("Canonical document processing failed:", error);

        // A source that has been acknowledged is still durable even if the
        // processing read times out. An upload that failed before source
        // acknowledgement must return to pick; it is never presented as
        // successfully handed off.
        if (uploadIdRef.current && canonicalSourceAttachedRef.current) {
          if (onMonitoringExtracted) {
            onMonitoringExtracted({});
            setMonitoringExtractedData(null);
            setStep("monitoringParsed");
          } else {
            onSave?.({
              fileName: file.name,
              resultType: "general",
              category,
              uploadId: uploadIdRef.current,
              documentId: null,
            });
            setStep("success");
          }
        } else {
          setErrorMessage(
            getDocumentUploadErrorMessage(error, failedProcessMessage),
          );
          setStep(onMonitoringExtracted ? "processingError" : "pick");
        }
      } finally {
        revokeSelectedFileObjectUrl(file);
      }
    },
    [
      slot,
      getUploadUrl,
      uploadFile,
      completeUpload,
      patientId,
      prefillFromOCR,
      failedProcessMessage,
      resetExtractedState,
      onMonitoringExtracted,
      onClose,
      onSave,
      queuedFiles.length,
      client,
      registerDurableUploadJob,
      takeDurableUploadJobItem,
      getIngestionContext,
    ],
  );

  const handleFilesSelected = useCallback(
    (files: SelectedFile[], category: string | null) => {
      if (files.length === 0) return;
      const [firstFile, ...restFiles] = files;
      queuedFilesRef.current.forEach(revokeSelectedFileObjectUrl);
      durableUploadJobIdRef.current = null;
      durableUploadJobItemsByNameRef.current = new Map();
      setQueuedFiles(restFiles);

      void (async () => {
        if (!patientId) {
          setErrorMessage(failedProcessMessage);
          setStep("pick");
          return;
        }
        const destination = destinationForUploadContext({
          slotId: slot?.id,
          category,
        });
        const job = await createUploadJob(client, patientId, {
          clientRequestId: createUploadJobIdempotencyKey(),
          source: "picker",
          sourceChannel: "patient_ui",
          intent: slot || onMonitoringExtracted ? "prefill" : "dump_anything",
          sourceContext: getIngestionContext(category),
          items: files.map((file) => ({
            name: file.name,
            contentType: resolveUploadContentType(file),
            sizeBytes: file.size ?? null,
            destination,
          })),
        });
        registerDurableUploadJob(job);

        void handleFileSelected(firstFile!, category);
      })().catch((error) => {
        setErrorMessage(
          getDocumentUploadErrorMessage(error, failedProcessMessage),
        );
        setStep("pick");
      });
    },
    [
      client,
      failedProcessMessage,
      handleFileSelected,
      onMonitoringExtracted,
      patientId,
      registerDurableUploadJob,
      slot,
      getIngestionContext,
    ],
  );

  const handleAddMoreFiles = useCallback(async () => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: allowAnyFile ? "*/*" : ["application/pdf", "image/*"],
        copyToCacheDirectory: true,
        multiple: true,
      });
      if (result.canceled || !result.assets || result.assets.length === 0) {
        return;
      }

      setQueuedFiles((current) => [
        ...current,
        ...result.assets.map((file) => ({
          name: file.name,
          uri: file.uri,
          mimeType: resolveUploadContentType({
            name: file.name,
            uri: file.uri,
            mimeType: file.mimeType,
          }),
          size: file.size ?? undefined,
        })),
      ]);
    } catch (error) {
      console.error("Error adding more document files:", error);
    }
  }, [allowAnyFile]);

  useEffect(() => {
    queuedFilesRef.current = queuedFiles;
  }, [queuedFiles]);

  useEffect(
    () => () => {
      queuedFilesRef.current.forEach(revokeSelectedFileObjectUrl);
    },
    [],
  );

  useEffect(() => {
    if (step !== "pick") return;
    const files =
      initialFiles && initialFiles.length > 0
        ? initialFiles
        : initialFile
          ? [initialFile]
          : [];
    if (files.length === 0) return;

    const filesKey = files.map((file) => file.uri).join("|");
    if (initialFileHandledRef.current === filesKey) return;
    initialFileHandledRef.current = filesKey;
    handleFilesSelected(files, selectedCategory);
  }, [handleFilesSelected, initialFile, initialFiles, selectedCategory, step]);

  const chooseAnotherFile = useCallback(async () => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: allowAnyFile ? "*/*" : ["application/pdf", "image/*"],
        copyToCacheDirectory: true,
        multiple: true,
      });
      if (result.canceled || !result.assets || result.assets.length === 0) {
        return;
      }
      setErrorMessage(null);
      handleFilesSelected(
        result.assets.map((file) => ({
          name: file.name,
          uri: file.uri,
          mimeType: resolveUploadContentType({
            name: file.name,
            uri: file.uri,
            mimeType: file.mimeType,
          }),
          size: file.size ?? undefined,
        })),
        selectedCategory,
      );
    } catch (error) {
      console.error("Error picking a replacement document:", error);
    }
  }, [allowAnyFile, handleFilesSelected, selectedCategory]);

  // Save the confirmed medication and appointment rows
  const handleSave = useCallback(async () => {
    setStep("saving");
    setErrorMessage(null);

    try {
      const uploadId = uploadIdRef.current;

      // Pre-validate the user-editable med and appointment rows BEFORE we
      // touch the API. Silently skipping a row used to make the appointment
      // / med vanish without explanation; instead block the save and tell
      // the user exactly which row is incomplete.
      const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
      const ISO_TIME = /^\d{2}:\d{2}$/;
      const invalidMed = medicationRows.find(
        (m) => m.name.trim() && (!m.startDate || !ISO_DATE.test(m.startDate)),
      );
      if (invalidMed) {
        setErrorMessage(
          t("documents.upload.error_med_missing_start_date", {
            params: { name: invalidMed.name.trim() },
          }),
        );
        setStep("review");
        return;
      }
      const invalidAppt = appointmentRows.find(
        (a) =>
          a.title.trim() &&
          (!a.date ||
            !ISO_DATE.test(a.date) ||
            !a.time ||
            !ISO_TIME.test(a.time)),
      );
      if (invalidAppt) {
        setErrorMessage(
          t("documents.upload.error_appt_missing_date_time", {
            params: { title: invalidAppt.title.trim() },
          }),
        );
        setStep("review");
        return;
      }

      // The canonical source orchestration has already persisted the
      // original file and owns the durable document root; only the
      // user-confirmed care rows are written here. Once this runs, a failed
      // row is retried on its own.
      primarySaveCompletedRef.current = true;

      const failedCareRows: string[] = [];

      // Create medications in the schedule
      const medsToCreate = medicationRows.filter(
        (m) =>
          m.name.trim() &&
          m.startDate &&
          !savedMedicationRowIdsRef.current.has(m.id),
      );
      for (const med of medsToCreate) {
        try {
          // An uploaded prescription can name a medication the patient has
          // never added. Resolve it against the catalog, not their history.
          let medicationId: string | undefined;
          try {
            const catalog = await withMedicationLookupDeadline(
              (signal) =>
                client.GET("/medication-catalog", {
                  signal,
                  params: {
                    query: {
                      q: med.name.trim(),
                      limit: 50,
                      ...(patientId ? { patientId } : {}),
                    },
                  },
                }),
              20_000,
            );
            if (!catalog.error) {
              medicationId = findMedicationByName(
                catalog.data?.data.items ?? [],
                med.name,
              )?.medicationId;
            }
          } catch {
            // Catalog availability must not discard a confirmed prescription.
            // The existing free-text creation path keeps the reviewed details.
          }
          await createMedicationMutation.mutateAsync({
            name: med.name,
            startDate: med.startDate,
            // An end date before the start is contradictory OCR output;
            // dropping it beats persisting an impossible course (#2220).
            endDate:
              med.endDate && med.endDate >= med.startDate ? med.endDate : null,
            dose: med.dosage ? parseFloat(med.dosage) : null,
            doseUnit: med.dosageUnit || null,
            timeTaken: med.timeOfDay ? [med.timeOfDay] : undefined,
            route: serializeMedicationRoute(med.route),
            form: serializeMedicationForm(med.form),
            notes: med.frequency || null,
            ...(medicationId ? { medicationId } : {}),
          });
          savedMedicationRowIdsRef.current.add(med.id);
        } catch (medError) {
          console.error("Failed to create medication:", med.name, medError);
          failedCareRows.push(
            `${t("documents.upload.derived_medication_label")}: ${med.name}`,
          );
        }
      }

      // Create appointments in the schedule. Previously these were only
      // stored as `appt:` tags on the document — invisible from the calendar.
      const apptsToCreate = appointmentRows.filter(
        (a) => a.title.trim() && !savedAppointmentRowIdsRef.current.has(a.id),
      );
      for (const appt of apptsToCreate) {
        // Derive a 1-hour end window so the backend, which requires
        // endDate/endTime, accepts the appointment.
        const parts = appt.time.split(":").map(Number);
        const hh = parts[0] ?? 0;
        const mm = parts[1] ?? 0;
        const endTime =
          hh >= 23
            ? "23:59"
            : `${String(hh + 1).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
        const payload = {
          title: appt.title,
          consultationType: appt.type ? [appt.type] : null,
          startDate: appt.date,
          startTime: appt.time,
          endDate: appt.date,
          endTime,
          description: appt.notes || null,
        };
        try {
          await createAppointmentMutation.mutateAsync(payload);
          savedAppointmentRowIdsRef.current.add(appt.id);
        } catch (apptError) {
          console.error(
            "[DocumentUploadModal] Failed to create appointment:",
            appt.title,
            apptError,
          );
          failedCareRows.push(
            `${t("documents.upload.derived_appointment_label")}: ${appt.title}`,
          );
        }
      }

      if (failedCareRows.length > 0) {
        setErrorMessage(
          t("documents.upload.error_derived_rows_failed", {
            params: { items: failedCareRows.join(", ") },
          }),
        );
        setStep("review");
        return;
      }

      const durableJobId = durableUploadJobIdRef.current;
      if (durableJobId && patientId && canonicalRunReadyRef.current) {
        try {
          await sendUploadJobCommand(client, patientId, durableJobId, {
            type: "acknowledge_job",
            idempotencyKey: createUploadJobIdempotencyKey(),
          });
        } catch (jobError) {
          // Saving the destination record remains successful even if the
          // activity acknowledgement is delayed; the next poll can still
          // recover the durable state.
          console.warn(
            "[DocumentUploadModal] Could not acknowledge durable upload job",
            jobError,
          );
        }
      }

      onSave?.({
        fileName: selectedFile?.name ?? "",
        resultType:
          medicationRows.some((m) => m.name.trim()) ||
          appointmentRows.some((a) => a.title.trim())
            ? "prescription"
            : "general",
        category: selectedCategory,
        uploadId: uploadId ?? null,
        documentId: createdDocumentIdRef.current,
      });
      setStep("success");
      if (queuedFiles.length === 0) {
        setTimeout(() => onClose(), 1200);
      }
    } catch (error) {
      console.error("Save failed:", error);
      setErrorMessage(t("documents.upload.error_failed_save"));
      setStep("review");
    }
  }, [
    selectedCategory,
    medicationRows,
    appointmentRows,
    createMedicationMutation,
    createAppointmentMutation,
    onClose,
    onSave,
    queuedFiles.length,
    selectedFile?.name,
    t,
    client,
    patientId,
  ]);

  const handleNextQueuedFile = useCallback(() => {
    const [nextFile, ...restFiles] = queuedFiles;
    if (!nextFile) {
      onClose();
      return;
    }
    setQueuedFiles(restFiles);
    void handleFileSelected(nextFile, selectedCategory);
  }, [handleFileSelected, onClose, queuedFiles, selectedCategory]);

  const processingItems: DocumentUploadProcessingItem[] = selectedFile
    ? [
        {
          id: selectedFile.uri,
          name: selectedFile.name,
          detail:
            step === "uploading"
              ? [
                  formatFileSize(selectedFile.size),
                  t("documents.upload.secure_upload"),
                ]
                  .filter(Boolean)
                  .join(" · ")
              : t("documents.upload.upload_complete_reading"),
          phase: step === "uploading" ? "uploading" : "reading",
        },
        ...queuedFiles.map((file) => ({
          id: file.uri,
          name: file.name,
          detail: t("documents.upload.queued_file_added"),
          phase: "queued" as const,
        })),
      ]
    : [];

  // ── Render steps ──

  if (
    step === "monitoringParsed" ||
    (step === "saving" && onMonitoringExtracted)
  ) {
    return (
      <MonitoringParsedStep
        fileName={selectedFile?.name ?? ""}
        valueCount={monitoringValueCount(monitoringExtractedData)}
        queuedFiles={queuedFiles}
        saving={step === "saving"}
        onContinue={() => void handleSave()}
      />
    );
  }

  if (step === "success") {
    return (
      <>
        <View style={{ gap: 12 }}>
          <DialogHeader
            leading={
              <Check size={22} color={EMBER.colors.text} strokeWidth={2.5} />
            }
            title={t("documents.upload.saved")}
          />
          {savedValueCount > 0 && (
            <DialogBodyText style={SUCCESS_DETAIL_STYLE}>
              {t("documents.upload.results_added_count", {
                params: { count: String(savedValueCount) },
              })}
            </DialogBodyText>
          )}
          {medicationRows.some((m) => m.name.trim()) && (
            <DialogBodyText style={SUCCESS_DETAIL_STYLE}>
              {t("documents.upload.medications_detected_count", {
                params: {
                  count: String(
                    medicationRows.filter((m) => m.name.trim()).length,
                  ),
                },
              })}
            </DialogBodyText>
          )}
          {queuedFiles.length > 0 ? (
            <>
              <DialogBodyText style={SUCCESS_DETAIL_STYLE}>
                {t("documents.upload.more_documents_pending_count", {
                  params: { count: String(queuedFiles.length) },
                })}
              </DialogBodyText>
              <View style={{ width: "100%", marginTop: 10 }}>
                <QueuedDocumentsCard files={queuedFiles} />
              </View>
              <CompactActionButton
                label={t("documents.upload.review_next_document")}
                action="documents.upload.review_next_document"
                variant="primary"
                onPress={handleNextQueuedFile}
                style={{ marginTop: 4, alignSelf: "flex-start" }}
              />
            </>
          ) : null}
        </View>
      </>
    );
  }

  if (step === "pick") {
    return (
      <PickStep
        slot={slot}
        defaultCategory={defaultCategory}
        onFilesSelected={handleFilesSelected}
        errorMessage={errorMessage}
        autoPickOnOpen={shouldAutoPickOnOpen}
        allowAnyFile={allowAnyFile}
      />
    );
  }

  if (step === "uploading" || step === "reading") {
    return (
      <DocumentUploadProcessingStep
        phase={step === "uploading" ? "uploading" : "reading"}
        items={processingItems}
        onAddMoreFiles={() => void handleAddMoreFiles()}
      />
    );
  }

  if (step === "processingError") {
    return (
      <ProcessingErrorStep
        fileName={selectedFile?.name ?? ""}
        message={errorMessage ?? failedProcessMessage}
        onChooseAnother={() => void chooseAnotherFile()}
      />
    );
  }

  if (step === "values" && valuesRun) {
    const finishValues = (savedCount: number) => {
      setSavedValueCount(savedCount);
      void (async () => {
        const durableJobId = durableUploadJobIdRef.current;
        // A confirmed document leaves the activity list; one put off for
        // later stays there, still inviting the review.
        if (savedCount > 0 && durableJobId && patientId) {
          try {
            await sendUploadJobCommand(client, patientId, durableJobId, {
              type: "acknowledge_job",
              idempotencyKey: createUploadJobIdempotencyKey(),
            });
          } catch (jobError) {
            console.warn(
              "[DocumentUploadModal] Could not acknowledge durable upload job",
              jobError,
            );
          }
        }
      })();
      onSave?.({
        fileName: selectedFile?.name ?? "",
        resultType: "values",
        category: selectedCategory,
        uploadId: uploadIdRef.current,
        documentId: createdDocumentIdRef.current,
      });
      setStep("success");
      if (queuedFiles.length === 0) {
        setTimeout(() => onClose(), 1200);
      }
    };
    const fileName = selectedFile?.name ?? "";
    return (
      <>
        <DialogHeader
          title={slot?.name ?? fileName.replace(/\.[^.]+$/, "")}
          subtitle={slot?.why}
          style={{ marginBottom: 18 }}
        />
        <ScrollView
          style={{ maxHeight: 520 }}
          showsVerticalScrollIndicator={false}
        >
          {/* The file just uploaded, as every document row in the app. */}
          <DocumentRow
            title={fileName}
            subtitle={formatFileSize(selectedFile?.size) || undefined}
            leading={
              <FileText size={17} color={EMBER.colors.warm} strokeWidth={1.6} />
            }
            elevated={false}
            testID="document-upload-file-row"
          />
          <View style={{ marginTop: 22 }}>
            <DocumentValuesReview
              run={valuesRun}
              sourceEntityId={valuesRun.sourceEntityId}
              documentName={slot?.name ?? fileName}
              documentDate={null}
              sampleTypes={isSemenAnalysisRun(valuesRun) ? ["semen"] : null}
              onConfirmed={finishValues}
              onNotNow={() => finishValues(0)}
            />
          </View>
          <QueuedDocumentsCard files={queuedFiles} />
        </ScrollView>
      </>
    );
  }

  // Review step
  return (
    <>
      {/* Back; the dialog draws the close */}
      <View
        style={{
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 12,
        }}
      >
        <BackLink
          label={t("documents.upload.back")}
          action="upload_back"
          onPress={() => {
            setStep("pick");
            setSelectedFile(null);
            uploadIdRef.current = null;
          }}
          style={{ alignSelf: "center" }}
        />
      </View>

      <DialogHeader
        leading={
          <Edit3 size={16} color={EMBER.colors.text} strokeWidth={1.5} />
        }
        title={t("documents.upload.review_title")}
        subtitle={t("documents.upload.review_subtitle")}
        style={{ marginBottom: 14 }}
      />

      {/* File indicator */}
      {selectedFile && (
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            gap: 8,
            marginBottom: 14,
          }}
        >
          <FileText size={14} color={EMBER.colors.warm} strokeWidth={1.5} />
          <Text
            className="font-dm-sans"
            numberOfLines={1}
            style={{
              flex: 1,
              fontSize: 12,
              color: EMBER.colors.textMid,
            }}
          >
            {selectedFile.name}
          </Text>
          {selectedFile.size ? (
            <Text
              className="font-dm-sans"
              style={{ fontSize: 11, color: EMBER.colors.textFaint }}
            >
              {formatFileSize(selectedFile.size)}
            </Text>
          ) : null}
        </View>
      )}

      <QueuedDocumentsCard files={queuedFiles} />

      {/* Error */}
      {errorMessage && <ErrorBanner message={errorMessage} />}

      {/* Medications and appointments the document proposes. Its measured
          values never reach this step: they have their own confirmation. */}
      <ScrollView
        style={{ maxHeight: 340 }}
        showsVerticalScrollIndicator={false}
      >
        <PrescriptionReview
          medications={medicationRows}
          setMedications={setMedicationRows}
          appointments={appointmentRows}
          setAppointments={setAppointmentRows}
          prescriber={prescriber}
          setPrescriber={setPrescriber}
          date={prescriptionDate}
          setDate={setPrescriptionDate}
          clinic={prescriptionClinic}
          setClinic={setPrescriptionClinic}
        />
      </ScrollView>

      {/* Save button */}
      <PrimaryButton
        action="documents.upload.save"
        label={
          step === "saving"
            ? t("documents.upload.saving")
            : primarySaveCompletedRef.current
              ? t("documents.upload.retry_care_rows")
              : t("documents.upload.save")
        }
        icon={
          step !== "saving" && primarySaveCompletedRef.current ? (
            <RotateCcw size={16} color="#FFFFFF" strokeWidth={2} />
          ) : undefined
        }
        onPress={handleSave}
        loading={step === "saving"}
        style={{ marginTop: 16 }}
      />
    </>
  );
}

// ── Exported Modal ──

export default function DocumentUploadModal({
  visible,
  onClose,
  onSave,
  slot = null,
  defaultCategory,
  defaultTags,
  ingestionContext,
  autoPickOnOpen,
  allowAnyFile,
  initialFile,
  initialFiles,
  onMonitoringExtracted,
}: Props) {
  if (!visible) return null;

  return (
    <Dialog
      visible={visible}
      onClose={onClose}
      animationType="fade"
      closeButton="minimal"
      closeAccessibilityLabel="Close upload modal"
      surfaceStyle={{ maxHeight: "100%" }}
      contentStyle={{
        paddingHorizontal: 24,
        paddingTop: 28,
        paddingBottom: 24,
      }}
      testID="document-upload-dialog"
      backdropTestID="document-upload-dialog-backdrop"
    >
      <ModalContent
        onClose={onClose}
        onSave={onSave}
        slot={slot}
        defaultCategory={defaultCategory}
        defaultTags={defaultTags}
        ingestionContext={ingestionContext}
        autoPickOnOpen={autoPickOnOpen}
        allowAnyFile={allowAnyFile}
        initialFile={initialFile}
        initialFiles={initialFiles}
        onMonitoringExtracted={onMonitoringExtracted}
      />
    </Dialog>
  );
}
